import { useEffect, useRef } from 'react';

export const RICH_TEXT_EDITOR_COLORS = [
  '#212121',
  '#424242',
  '#757575',
  '#d32f2f',
  '#b71c1c',
  '#1565c0',
  '#2e7d32',
  '#e65100',
];

export const RICH_TEXT_EDITOR_BACKGROUNDS = [
  '#ffffff',
  '#fafbfc',
  '#fff3e0',
  '#e8f5e9',
  '#e3f2fd',
  '#fce4ec',
  '#f3e5f5',
  '#fffde7',
];

export const RICH_TEXT_EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: RICH_TEXT_EDITOR_COLORS }, { background: RICH_TEXT_EDITOR_BACKGROUNDS }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['blockquote', { align: [] }],
    ['link'],
    ['clean'],
  ],
};

export const RICH_TEXT_EDITOR_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'list',
  'bullet',
  'indent',
  'blockquote',
  'align',
  'link',
];

const RICH_TEXT_TOOLBAR_BUTTON_TITLES = {
  bold: 'Krepko',
  italic: 'Ležeče',
  underline: 'Podčrtano',
  strike: 'Prečrtano',
  blockquote: 'Citat',
  link: 'Povezava',
  clean: 'Počisti oblikovanje',
};

const RICH_TEXT_TOOLBAR_LIST_TITLES = {
  ordered: 'Oštevilčen seznam',
  bullet: 'Seznam z oznakami',
};

const RICH_TEXT_TOOLBAR_INDENT_TITLES = {
  '-1': 'Zmanjšaj zamik',
  '+1': 'Povečaj zamik',
};

const RICH_TEXT_TOOLBAR_PICKER_TITLES = {
  header: 'Naslov',
  color: 'Barva besedila',
  background: 'Barva ozadja',
  align: 'Poravnava',
  font: 'Pisava',
  size: 'Velikost',
};

/** Native hover titles for Quill toolbar controls (B → Krepko, I → Ležeče, …). */
export function applyRichTextToolbarTitles(root) {
  if (!root) return;

  root.querySelectorAll('.ql-toolbar button').forEach((button) => {
    if (button.classList.contains('ql-picker-label')) return;

    const actionClass = Array.from(button.classList).find(
      (cls) => cls.startsWith('ql-') && cls !== 'ql-picker',
    );
    if (!actionClass) return;

    const action = actionClass.slice(3);
    const value = button.getAttribute('value');

    if (action === 'list' && value) {
      button.setAttribute('title', RICH_TEXT_TOOLBAR_LIST_TITLES[value] || value);
      return;
    }

    if (action === 'indent' && value) {
      button.setAttribute('title', RICH_TEXT_TOOLBAR_INDENT_TITLES[value] || value);
      return;
    }

    if (RICH_TEXT_TOOLBAR_BUTTON_TITLES[action]) {
      button.setAttribute('title', RICH_TEXT_TOOLBAR_BUTTON_TITLES[action]);
    }
  });

  root.querySelectorAll('.ql-toolbar .ql-picker').forEach((picker) => {
    const pickerClass = Array.from(picker.classList).find(
      (cls) => cls.startsWith('ql-') && cls !== 'ql-picker' && cls !== 'ql-expanded',
    );
    if (!pickerClass) return;

    const action = pickerClass.slice(3);
    const label = picker.querySelector('.ql-picker-label');
    if (label && RICH_TEXT_TOOLBAR_PICKER_TITLES[action]) {
      label.setAttribute('title', RICH_TEXT_TOOLBAR_PICKER_TITLES[action]);
    }
  });
}

export function useRichTextToolbarTitles(active = true) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;

    applyRichTextToolbarTitles(containerRef.current);
    return undefined;
  }, [active]);

  return containerRef;
}

export function isRichTextHtml(value) {
  return /<[a-z][\s\S]*>/i.test(value || '');
}

export function isRichTextEmpty(value) {
  const stripped = (value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return stripped.length === 0;
}

export function plainTextToHtml(value) {
  const text = (value || '').trim();
  if (!text) return '';
  if (isRichTextHtml(text)) return text;

  return text
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export function toDisplayHtml(value) {
  return plainTextToHtml(value);
}
